import React, { useEffect, useState } from "react";
import { Button, Card, Modal, ModalFooter, ModalHeader, Spinner } from "reactstrap";
import { VsiBubble, VsiH4 } from "../../../styled/App.styled";
import { useVsiAppContext } from "../../../stores/VsiApp";
import { virtualClusterPlansDefs } from "../../../utils/gridColumnDefs/virtual/virtualUnits";
import Grid from "../../../components/Grid";
import { useDataWithRoute, useTitleFromComponent } from "../../../utils/hooks";
import { useNavigate } from "react-router-dom";
import { DeleteAuth } from "../../../utils/Verbs";
import swal from "sweetalert";

export default function VirtualOrphanedVMDKs({ context, title }) {

  useTitleFromComponent(`${context} / ${title}`);

  const { theme, client, periodId, user, getAccessTokenSilently } = useVsiAppContext();
  const [projectsData, setProjectsData] = useState([]);
  const [rowDeleted, setRowDeleted] = useState(undefined);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [rowSelected, setRowSelected] = useState(null);

  let navigate = useNavigate();

  const [{ recordset: clusterPlansData }, { isLoading }] = useDataWithRoute(`/api/v6/admin/virtual_cluster_capacity_plan/${client.clientId}`, rowDeleted);

  const onRowClick = (e) => {
    navigate(`/reports/virtual/cluster/cluster-modeling?cluster_name=${encodeURI(e.data.clusterName)}&vcenter_name=${encodeURI(e.data.deviceName)}&period_id=${encodeURI(periodId)}`);
  };

  const onSelectionChanged = (gridRef) => {
    const selectedRow = gridRef?.current?.api?.getSelectedRows?.()?.[0];
    if (selectedRow) {
      setRowSelected(selectedRow);
    }
  };

  useEffect(() => {
    let adjustments = [];
    for (let i = 0; i < clusterPlansData.length; i++) {
      let customHostsCost = 0;
      clusterPlansData[i]["customHostModels"].forEach(customModel => {
        customHostsCost += customModel.costChange;
      });
      clusterPlansData[i]["adjustments"].forEach(adjustment => {
        let rearrangedAdjustment = {
          "_id": clusterPlansData[i]["_id"],
          "deviceName": clusterPlansData[i]["deviceName"],
          "clusterName": clusterPlansData[i]["clusterName"],
          "clientId": clusterPlansData[i]["clientId"],
          "changedByUserEmail": clusterPlansData[i]["changedByUserEmail"],
          "notes": clusterPlansData[i]["notes"],
          "projectName": clusterPlansData[i]["projectName"],
          "date": adjustment["actualDate"],
          "addedCost": adjustment["costChange"] + customHostsCost,
          "adjustments": []
        };

        if (adjustment["models"].length && adjustment["models"][0]?.["model"]) {
          adjustment["models"].map(model => rearrangedAdjustment["adjustments"].push(`${model["model"]}: ${model["modelCountAdded"] >= 0 ? "+" : "-"}${model["modelCountAdded"]}`));
        }

        if (adjustment["customCapacity"]) {
          rearrangedAdjustment["adjustments"].push(` Capacity: 
                        cpu: ${adjustment["customCapacity"]["cpu"] >= 0 ? "+" : "-"}${adjustment["customCapacity"]["cpu"]}, 
                        disk: ${adjustment["customCapacity"]["disk"] >= 0 ? "+" : "-"}${adjustment["customCapacity"]["disk"]}, 
                        mem: ${adjustment["customCapacity"]["mem"] >= 0 ? "+" : "-"}${adjustment["customCapacity"]["mem"]}, 
                        vm: ${adjustment["customCapacity"]["vm"] >= 0 ? "+" : "-"}${adjustment["customCapacity"]["vm"]}`);
        }

        if (adjustment["customWorkload"]) {
          rearrangedAdjustment["adjustments"].push(` Workload: 
                        cost: ${adjustment["customWorkload"]["cost"] >= 0 ? "+" : "-"}${adjustment["customWorkload"]["cost"]}, 
                        cpu: ${adjustment["customWorkload"]["cpu"] >= 0 ? "+" : "-"}${adjustment["customWorkload"]["cpu"]}, 
                        disk: ${adjustment["customWorkload"]["disk"] >= 0 ? "+" : "-"}${adjustment["customWorkload"]["disk"]}, 
                        mem: ${adjustment["customWorkload"]["mem"] >= 0 ? "+" : "-"}${adjustment["customWorkload"]["mem"]}, 
                        vm: ${adjustment["customWorkload"]["vm"] >= 0 ? "+" : "-"}${adjustment["customWorkload"]["vm"]}`);
        }

        adjustments.push(rearrangedAdjustment);
      });
    }
    setProjectsData(adjustments);
  }, [clusterPlansData]);

  const deleteProject = async (e) => {
    e.preventDefault();
    const token = await getAccessTokenSilently();
    DeleteAuth(`/api/v6/admin/virtual_cluster_capacity_plan/${rowSelected._id}`, token)
      .then(data => {
        if (data.status === 200) {
          setRowDeleted(data.data.data);
          setShowDeleteModal(false);

          swal({
            title: `Project Deleted Successfully.`,
            text: "Project Deleted Successfully."
          });
        } else {
          setShowDeleteModal(false);
          console.error(data);
        }
      })
      .catch(err => {
        setShowDeleteModal(false);
        swal({
          title: `Error deleting the project.`,
          text: "Please try again later."
        });
        console.error(err);
      });
  };

  const deleteProjectBtn = <>
    <Button
      onClick={() => {
        setShowDeleteModal(true);
      }}
      id={"upload-trash-btn"}
      disabled={!rowSelected}>
      <i className="fa fa-trash" aria-hidden="true"></i>
    </Button>
    <Modal className="warning-modal" backdrop="static" isOpen={showDeleteModal}>
      <ModalHeader toggle={() => setShowDeleteModal(false)}>Are you sure you want to delete
        the selected Project</ModalHeader>
      <ModalFooter>
        {rowSelected && <Button onClick={deleteProject} color="danger">Confirm Delete</Button>
        }
        <Button color="secondary" onClick={() => {
          setShowDeleteModal(false);
        }}>Cancel</Button>
      </ModalFooter>
    </Modal>
  </>;

  return (
    <Card className={`report-card p-2 nav-card-${theme}`}>
      <VsiH4>{`Virtual Cluster Plans Summary`}</VsiH4>
      {isLoading ? <Spinner color={"primary"} /> : (
        <VsiBubble>
          <Grid addStyle data={projectsData}
                gridText={"Double click to see capacity plan"}
                columnDefs={virtualClusterPlansDefs}
                rowSelection={"single"}
                onSelectionChanged={onSelectionChanged}
                customToolbarButton={deleteProjectBtn}
                rowDoubleClicked={onRowClick} />
        </VsiBubble>
      )}
    </Card>
  );
}
